/**
 * Part 17 — actionable Xero export error messages.
 *
 * Prefer a concrete next step over a generic "Something went wrong" when OMS
 * has enough signal (auth, account code, tax mapping, duplicate, attachment).
 */

export const XERO_EXPORT_ERROR_CODE = Object.freeze({
  CONNECTION_EXPIRED: 'connection_expired',
  API_UNAVAILABLE: 'api_unavailable',
  ACCOUNT_CODE_MISSING: 'account_code_missing',
  TAX_MAPPING_MISSING: 'tax_mapping_missing',
  ALREADY_EXPORTED: 'already_exported',
  ATTACHMENT_FAILED: 'attachment_failed',
  ALLOCATION_FAILED: 'allocation_failed',
  VALIDATION: 'validation',
  MISSING_ORGANIZATION: 'missing_organization',
  MISSING_DEFAULTS: 'missing_defaults',
  VAT_ROUNDING: 'vat_rounding',
  UNKNOWN: 'unknown',
})

const ACCOUNT_CODE_RE =
  /Account\s*(?:code|ID)?\s*['"`]?([A-Z0-9_-]+)['"`]?\s*(?:is not|was not|could not be|not)\s*(?:found|valid|a valid)/i
const ACCOUNT_CODE_ALT_RE =
  /(?:The\s+)?(?:account|AccountID)\s+['"`]?([A-Z0-9_-]+)['"`]?\s+(?:does not exist|is invalid|not found)/i
const TAX_TYPE_RE =
  /TaxType\s*['"`]?([A-Z0-9_-]+)['"`]?\s*(?:is not|was not|could not be|not)\s*(?:found|valid|a valid)|(?:tax\s*type|tax\s*rate).*(?:not\s*(?:found|configured|mapped)|invalid)/i
const DUPLICATE_INVOICE_RE =
  /Invoice\s*#?\s*['"`]?([^'"`\s]+)['"`]?\s*(?:already exists|has already been entered)|already\s+(?:been\s+)?(?:exported|entered|exists)|duplicate.*invoice/i
const AUTH_RE =
  /unauthorized|unauthorised|invalid_grant|token.*(expired|revoked|invalid)|authentication.*(failed|required)|access.?denied|forbidden/i

function flattenMessages(value, out = []) {
  if (value == null) return out
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (trimmed) out.push(trimmed)
    return out
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    out.push(String(value))
    return out
  }
  if (Array.isArray(value)) {
    value.forEach(item => flattenMessages(item, out))
    return out
  }
  if (typeof value === 'object') {
    if (value.Message) flattenMessages(value.Message, out)
    if (value.message) flattenMessages(value.message, out)
    if (value.Detail) flattenMessages(value.Detail, out)
    if (value.title) flattenMessages(value.title, out)
    if (value.error) flattenMessages(value.error, out)
    if (value.userMessage) flattenMessages(value.userMessage, out)
    if (Array.isArray(value.ValidationErrors)) {
      value.ValidationErrors.forEach(v => flattenMessages(v, out))
    }
    if (Array.isArray(value.Elements)) {
      value.Elements.forEach(el => flattenMessages(el, out))
    }
    if (value.response?.data) flattenMessages(value.response.data, out)
    if (value.data && value.data !== value) flattenMessages(value.data, out)
  }
  return out
}

export function collectXeroErrorMessages(errorOrPayload) {
  return [...new Set(flattenMessages(errorOrPayload).filter(Boolean))]
}

function httpStatusOf(errorOrPayload) {
  return (
    Number(errorOrPayload?.response?.status) ||
    Number(errorOrPayload?.status) ||
    Number(errorOrPayload?.statusCode) ||
    null
  )
}

function matchAccountCode(text) {
  const m = text.match(ACCOUNT_CODE_RE) || text.match(ACCOUNT_CODE_ALT_RE)
  return m?.[1] || null
}

function matchTaxType(text) {
  const m = text.match(TAX_TYPE_RE)
  return m?.[1] || null
}

function matchDuplicateInvoice(text) {
  const m = text.match(DUPLICATE_INVOICE_RE)
  return m?.[1] || null
}

/**
 * @returns {{
 *   code: string,
 *   message: string,
 *   detail?: string,
 *   canForceSend?: boolean,
 *   retryAttachment?: boolean,
 * }}
 */
export function formatActionableXeroExportError(
  errorOrPayload,
  {
    invoiceNumber = '',
    isCreditNote = false,
    partialStep = null,
    organizationName = '',
  } = {},
) {
  const status = httpStatusOf(errorOrPayload)
  const messages = collectXeroErrorMessages(errorOrPayload)
  const joined = messages.join(' | ')
  const invLabel = String(invoiceNumber || '').trim()
  const docWord = isCreditNote ? 'Credit note' : 'Supplier invoice'
  const orgLabel = String(organizationName || '').trim()

  // Partial post-create failures (Part 16) — document already exists.
  if (partialStep === 'auditAttachment' || partialStep === 'supplierAttachments') {
    return {
      code: XERO_EXPORT_ERROR_CODE.ATTACHMENT_FAILED,
      message: invLabel
        ? `${docWord} ${invLabel} exported successfully, but supporting attachment failed. Retry attachment.`
        : 'Bill exported successfully, but supporting attachment failed. Retry attachment.',
      detail: joined || undefined,
      retryAttachment: true,
      canForceSend: false,
    }
  }
  if (partialStep === 'creditAllocation') {
    return {
      code: XERO_EXPORT_ERROR_CODE.ALLOCATION_FAILED,
      message: invLabel
        ? `Credit note ${invLabel} was created in Xero, but allocation to the supplier bill failed. Retry allocation.`
        : 'Credit note was created in Xero, but allocation to the supplier bill failed. Retry allocation.',
      detail: joined || undefined,
      canForceSend: false,
    }
  }
  if (partialStep === 'historyNote') {
    return {
      code: XERO_EXPORT_ERROR_CODE.ATTACHMENT_FAILED,
      message: invLabel
        ? `${docWord} ${invLabel} exported successfully, but the OMS history note failed. Retry export to finish remaining steps.`
        : 'Bill exported successfully, but the OMS history note failed. Retry export to finish remaining steps.',
      detail: joined || undefined,
      canForceSend: false,
    }
  }

  // Auth / connection
  if (
    status === 401 ||
    status === 403 ||
    AUTH_RE.test(joined) ||
    errorOrPayload?.code === XERO_EXPORT_ERROR_CODE.CONNECTION_EXPIRED
  ) {
    return {
      code: XERO_EXPORT_ERROR_CODE.CONNECTION_EXPIRED,
      message:
        'Xero connection has expired — reconnect Xero under Integrations, then try again.',
      detail: joined || undefined,
      canForceSend: false,
    }
  }

  // API / network unavailable
  if (
    status >= 500 ||
    status === 429 ||
    /ECONNREFUSED|ENOTFOUND|ETIMEDOUT|ECONNRESET|network|unavailable|service.?unavailable|too many requests/i.test(
      joined,
    ) ||
    errorOrPayload?.code === 'ECONNABORTED'
  ) {
    return {
      code: XERO_EXPORT_ERROR_CODE.API_UNAVAILABLE,
      message:
        'Xero API is temporarily unavailable. Wait a moment, then retry the export.',
      detail: joined || undefined,
      canForceSend: false,
    }
  }

  // Already exported / duplicate
  const dupNum = matchDuplicateInvoice(joined) || invLabel
  if (
    DUPLICATE_INVOICE_RE.test(joined) ||
    errorOrPayload?.code === XERO_EXPORT_ERROR_CODE.ALREADY_EXPORTED ||
    errorOrPayload?.alreadySent
  ) {
    return {
      code: XERO_EXPORT_ERROR_CODE.ALREADY_EXPORTED,
      message: dupNum
        ? `${docWord} ${dupNum} appears to have already been exported.`
        : `${docWord} appears to have already been exported.`,
      detail: joined || undefined,
      canForceSend: false,
    }
  }

  // Account code
  const accountCode = matchAccountCode(joined)
  if (accountCode || /Account code|AccountID|nominal/i.test(joined)) {
    return {
      code: XERO_EXPORT_ERROR_CODE.ACCOUNT_CODE_MISSING,
      message: accountCode
        ? `Account code ${accountCode} could not be found in Xero. Check the supplier or product nominal mapping, then retry.`
        : 'A mapped account code could not be found in Xero. Check supplier/product nominal mappings, then retry.',
      detail: joined || undefined,
      canForceSend: true,
    }
  }

  // Tax / VAT mapping
  const taxType = matchTaxType(joined)
  if (
    taxType ||
    /tax\s*mapping|vatTaxTypeMap|No Xero tax rate mapped|Unknown OMS VAT treatment|TaxType/i.test(
      joined,
    )
  ) {
    const treatmentHint = /No VAT|none|exempt/i.test(joined)
      ? 'No VAT'
      : /zero.?rated/i.test(joined)
        ? 'Zero-rated'
        : /standard/i.test(joined)
          ? 'Standard'
          : null
    if (treatmentHint) {
      return {
        code: XERO_EXPORT_ERROR_CODE.TAX_MAPPING_MISSING,
        message: orgLabel
          ? `${treatmentHint} tax mapping is not configured for Xero organisation "${orgLabel}". Configure VAT tax types under Integrations, then retry.`
          : `${treatmentHint} tax mapping is not configured for this Xero organisation. Configure VAT tax types under Integrations, then retry.`,
        detail: joined || undefined,
        canForceSend: false,
      }
    }
    return {
      code: XERO_EXPORT_ERROR_CODE.TAX_MAPPING_MISSING,
      message: taxType
        ? `Xero tax type ${taxType} could not be used. Check VAT tax mapping for this organisation under Integrations, then retry.`
        : orgLabel
          ? `VAT tax mapping is not configured for Xero organisation "${orgLabel}". Configure it under Integrations, then retry.`
          : 'VAT tax mapping is not configured for this Xero organisation. Configure it under Integrations, then retry.',
      detail: joined || undefined,
      canForceSend: false,
    }
  }

  // Material VAT rounding (Part 10)
  if (/material|VAT.*round|rounding.*(fail|differ)|tax amount/i.test(joined)) {
    return {
      code: XERO_EXPORT_ERROR_CODE.VAT_ROUNDING,
      message:
        joined ||
        'Supplier VAT does not match expected VAT within tolerance. Check invoice net/VAT totals, then retry.',
      detail: joined || undefined,
      canForceSend: false,
    }
  }

  // Missing org / defaults from known OMS messages
  if (/No Xero organisation|organisation available/i.test(joined)) {
    return {
      code: XERO_EXPORT_ERROR_CODE.MISSING_ORGANIZATION,
      message:
        'No Xero organisation is available to send to. Check Integrations, then retry.',
      detail: joined || undefined,
      canForceSend: false,
    }
  }
  if (/missing a mapped Xero account or contact|no default Xero/i.test(joined)) {
    return {
      code: XERO_EXPORT_ERROR_CODE.MISSING_DEFAULTS,
      message:
        'Supplier is missing a mapped Xero account or contact. Map them under Your Suppliers, then retry.',
      detail: joined || undefined,
      canForceSend: true,
    }
  }

  // Prefer the first concrete Xero validation message when present
  const firstUseful = messages.find(
    m =>
      m &&
      !/^error creating bill$/i.test(m) &&
      !/^xero validation errors$/i.test(m) &&
      !/^something went wrong/i.test(m) &&
      !/^unknown error$/i.test(m) &&
      !/^there was an error/i.test(m),
  )
  if (firstUseful) {
    return {
      code: XERO_EXPORT_ERROR_CODE.VALIDATION,
      message: firstUseful,
      detail: messages.length > 1 ? messages.join(' | ') : undefined,
      canForceSend: true,
    }
  }

  return {
    code: XERO_EXPORT_ERROR_CODE.UNKNOWN,
    message:
      'Xero export failed. Check the Xero connection and mappings under Integrations, then retry.',
    detail: joined || undefined,
    canForceSend: true,
  }
}

/**
 * Build the already-exported user message (complete or legacy).
 */
export function formatAlreadyExportedMessage({
  invoiceNumber = '',
  isCreditNote = false,
} = {}) {
  const inv = String(invoiceNumber || '').trim()
  const docWord = isCreditNote ? 'Credit note' : 'Supplier invoice'
  return inv
    ? `${docWord} ${inv} appears to have already been exported.`
    : `${docWord} appears to have already been exported.`
}

/**
 * Partial-success banner when create worked but steps remain.
 */
export function formatPartialExportSuccessMessage({
  invoiceNumber = '',
  isCreditNote = false,
  steps = {},
} = {}) {
  const inv = String(invoiceNumber || '').trim()
  const docWord = isCreditNote ? 'Credit note' : 'Bill'
  const lastStep = String(steps?.lastErrorStep || '').trim()

  if (
    lastStep === 'auditAttachment' ||
    lastStep === 'supplierAttachments' ||
    steps?.auditAttachment === false
  ) {
    return formatActionableXeroExportError(null, {
      invoiceNumber: inv,
      isCreditNote,
      partialStep: 'auditAttachment',
    }).message
  }
  if (
    lastStep === 'creditAllocation' ||
    (isCreditNote &&
      steps?.creditAllocation === false &&
      !steps?.creditAllocationNotApplicable)
  ) {
    return formatActionableXeroExportError(null, {
      invoiceNumber: inv,
      isCreditNote: true,
      partialStep: 'creditAllocation',
    }).message
  }
  if (lastStep === 'historyNote' || steps?.historyNote === false) {
    return formatActionableXeroExportError(null, {
      invoiceNumber: inv,
      isCreditNote,
      partialStep: 'historyNote',
    }).message
  }
  return inv
    ? `${docWord} ${inv} exported with some steps still incomplete. Retry to finish.`
    : `${docWord} exported with some steps still incomplete. Retry to finish.`
}

/**
 * Prefer server userMessage / message; never collapse to a useless generic when
 * a structured error payload is present.
 */
export function pickActionableXeroClientMessage(resBody = {}, fallbackError = null) {
  if (resBody?.userMessage && typeof resBody.userMessage === 'string') {
    return resBody.userMessage
  }
  if (
    resBody?.message &&
    typeof resBody.message === 'string' &&
    !/^error creating bill$/i.test(resBody.message) &&
    !/^xero validation errors$/i.test(resBody.message) &&
    !/^something went wrong/i.test(resBody.message)
  ) {
    return resBody.message
  }
  const formatted = formatActionableXeroExportError(
    resBody?.error || resBody || fallbackError,
    {
      invoiceNumber: resBody?.invoiceNumber || '',
      isCreditNote: resBody?.xeroInvoiceType === 'CreditNote',
    },
  )
  return formatted.message
}
